import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LayoutDashboard, FileText, LineChart, RotateCw, Menu } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

/**
 * Bottom tab bar per mobile - accessibile con una mano.
 * Mostrata solo su schermi < 1024px (`lg:hidden`).
 * Le voci sono scelte tra le piu' usate: Dashboard, Pratiche, Comparatore,
 * Rinnovi (con badge) e un tasto "Menu" che apre la sidebar completa.
 */
const BottomNav = ({ onOpenMenu, rinnoviCount = 0 }) => {
  const { user, hasRole } = useAuth();
  const location = useLocation();

  if (!user) return null;

  const items = [
    {
      href: '/dashboard',
      label: 'Home',
      icon: LayoutDashboard,
      roles: ['super_admin', 'sub_agent', 'back_office', 'interno', 'operatore_call'],
      testid: 'bottom-nav-dashboard',
    },
    {
      href: '/contracts',
      label: 'Pratiche',
      icon: FileText,
      roles: ['super_admin', 'sub_agent', 'back_office', 'interno'],
      testid: 'bottom-nav-contracts',
    },
    {
      href: '/comparatore',
      label: 'Confronto',
      icon: LineChart,
      roles: ['super_admin', 'sub_agent', 'back_office', 'interno'],
      testid: 'bottom-nav-comparatore',
    },
    {
      href: '/rinnovi',
      label: 'Rinnovi',
      icon: RotateCw,
      roles: ['super_admin', 'sub_agent', 'back_office', 'interno'],
      badge: rinnoviCount,
      testid: 'bottom-nav-rinnovi',
    },
  ];

  const visible = items.filter(it => hasRole(it.roles));

  const isActive = (href) => location.pathname === href || location.pathname.startsWith(href + '/');

  return (
    <nav
      className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md border-t border-slate-200 dark:border-slate-800 safe-bottom safe-left safe-right shadow-[0_-4px_20px_rgba(15,23,42,0.06)]"
      data-testid="mobile-bottom-nav"
    >
      <div className="flex items-stretch justify-around min-h-[60px] px-1">
        {visible.map((it) => {
          const active = isActive(it.href);
          const Icon = it.icon;
          return (
            <Link
              key={it.href}
              to={it.href}
              data-testid={it.testid}
              className={`relative flex-1 flex flex-col items-center justify-center gap-0.5 py-1.5 min-w-0 transition-colors ${
                active
                  ? 'text-sky-600 dark:text-sky-400'
                  : 'text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'
              }`}
            >
              <span className="relative">
                <Icon className={`w-5 h-5 ${active ? 'scale-110' : ''} transition-transform`} strokeWidth={active ? 2.4 : 2} />
                {typeof it.badge === 'number' && it.badge > 0 && (
                  <span className="absolute -top-1.5 -right-2 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center leading-none">
                    {it.badge > 99 ? '99+' : it.badge}
                  </span>
                )}
              </span>
              <span className={`text-[10px] leading-none ${active ? 'font-semibold' : 'font-medium'}`}>{it.label}</span>
              {active && (
                <span className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 rounded-b-full bg-sky-500 dark:bg-sky-400" aria-hidden="true" />
              )}
            </Link>
          );
        })}
        <button
          type="button"
          onClick={onOpenMenu}
          data-testid="bottom-nav-menu"
          className="flex-1 flex flex-col items-center justify-center gap-0.5 py-1.5 min-w-0 text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 transition-colors"
          aria-label="Apri menu"
        >
          <Menu className="w-5 h-5" strokeWidth={2} />
          <span className="text-[10px] leading-none font-medium">Menu</span>
        </button>
      </div>
    </nav>
  );
};

export default BottomNav;
